import { useRef, useEffect, useState, useCallback } from "react";
import type { GalaxyState, GalaxySystem } from "../../types/galaxy";
import { FACTION_COLORS } from "../../types/galaxy";

interface Props {
  galaxyState: GalaxyState;
  selectedSystem: string | null;
  onSelectSystem: (systemId: string | null) => void;
  onSetCourse: (systemId: string) => void;
}

interface Tooltip {
  x: number;
  y: number;
  lines: string[];
}

const SPECTRAL_COLORS: Record<string, string> = {
  O: "#9bb0ff", B: "#aabfff", A: "#cad7ff", F: "#f8f7ff",
  G: "#fff4e0", K: "#ffd2a1", M: "#ffcc6f",
};

export function MapCanvas({ galaxyState, selectedSystem, onSelectSystem, onSetCourse }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tooltip, setTooltip] = useState<Tooltip | null>(null);
  const [canvasSize, setCanvasSize] = useState({ w: 800, h: 600 });

  // Pan/zoom state
  const panRef = useRef({ x: 0, y: 0 });
  const zoomRef = useRef(1);
  const isDraggingRef = useRef(false);
  const lastMouseRef = useRef({ x: 0, y: 0 });

  // Resize
  useEffect(() => {
    const container = canvasRef.current?.parentElement;
    if (!container) return;
    const resize = () => {
      const rect = container.getBoundingClientRect();
      setCanvasSize({ w: Math.floor(rect.width), h: Math.floor(rect.height) });
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // Get screen position for a system
  const getSystemScreenPos = useCallback((sys: GalaxySystem, W: number, H: number) => {
    const CX = W / 2 + panRef.current.x;
    const CY = H / 2 + panRef.current.y;
    const scale = getScale(galaxyState, W, H) * zoomRef.current;
    return {
      x: CX + sys.position.x * scale,
      y: CY - sys.position.y * scale,
    };
  }, [galaxyState]);

  // Mouse handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDraggingRef.current = true;
    lastMouseRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isDraggingRef.current) {
      panRef.current.x += e.clientX - lastMouseRef.current.x;
      panRef.current.y += e.clientY - lastMouseRef.current.y;
      lastMouseRef.current = { x: e.clientX, y: e.clientY };
      setTooltip(null);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * (canvas.width / rect.width);
    const my = (e.clientY - rect.top) * (canvas.height / rect.height);

    const W = canvas.width, H = canvas.height;
    let found: Tooltip | null = null;

    for (const sys of galaxyState.systems) {
      const pos = getSystemScreenPos(sys, W, H);
      if (Math.abs(mx - pos.x) < 16 && Math.abs(my - pos.y) < 16) {
        const presences = galaxyState.factionPresence
          .filter(p => p.systemId === sys.id && p.level !== "none")
          .map(p => `${p.factionId}: ${p.level}`);

        const lines = [
          sys.name,
          `Class ${sys.spectralClass} — ${sys.region}`,
        ];
        if (sys.faction) lines.push(`Control: ${sys.faction}`);
        if (presences.length) lines.push(...presences);
        found = { x: pos.x, y: pos.y, lines };
        break;
      }
    }
    setTooltip(found);
  }, [galaxyState, getSystemScreenPos]);

  const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isDraggingRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * (canvas.width / rect.width);
    const my = (e.clientY - rect.top) * (canvas.height / rect.height);
    const W = canvas.width, H = canvas.height;

    for (const sys of galaxyState.systems) {
      const pos = getSystemScreenPos(sys, W, H);
      if (Math.abs(mx - pos.x) < 16 && Math.abs(my - pos.y) < 16) {
        onSelectSystem(sys.id);
        return;
      }
    }
    onSelectSystem(null);
  }, [galaxyState, getSystemScreenPos, onSelectSystem]);

  const handleDoubleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * (canvas.width / rect.width);
    const my = (e.clientY - rect.top) * (canvas.height / rect.height);
    const W = canvas.width, H = canvas.height;

    for (const sys of galaxyState.systems) {
      const pos = getSystemScreenPos(sys, W, H);
      if (Math.abs(mx - pos.x) < 16 && Math.abs(my - pos.y) < 16) {
        onSetCourse(sys.id);
        return;
      }
    }
  }, [galaxyState, getSystemScreenPos, onSetCourse]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    zoomRef.current = Math.max(0.3, Math.min(3, zoomRef.current * delta));
  }, []);

  // Drawing loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvasSize.w;
    canvas.height = canvasSize.h;

    let animId = 0;
    let frame = 0;
    const draw = () => {
      animId = requestAnimationFrame(draw);
      if (++frame % 3 !== 0) return; // ~20fps

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const W = canvas.width, H = canvas.height;

      drawMap(ctx, W, H, galaxyState, selectedSystem, panRef.current, zoomRef.current);
    };

    draw();
    return () => cancelAnimationFrame(animId);
  }, [canvasSize, galaxyState, selectedSystem]);

  return (
    <div className="galaxy-map-canvas-wrap">
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => { isDraggingRef.current = false; setTooltip(null); }}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onWheel={handleWheel}
        style={{ cursor: isDraggingRef.current ? "grabbing" : "crosshair" }}
      />
      {tooltip && (
        <div
          className="galaxy-map-tooltip"
          style={{ left: tooltip.x + 16, top: tooltip.y - 8 }}
        >
          {tooltip.lines.map((line, i) => (
            <div key={i} className={i === 0 ? "tt-name" : "tt-stat"}>{line}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Drawing helpers ──

function getScale(state: GalaxyState, W: number, H: number): number {
  let maxR = 1;
  for (const s of state.systems) {
    const r = Math.sqrt(s.position.x ** 2 + s.position.y ** 2);
    if (r > maxR) maxR = r;
  }
  return (Math.min(W, H) / 2 - 80) / maxR;
}

function drawMap(
  ctx: CanvasRenderingContext2D,
  W: number, H: number,
  state: GalaxyState,
  selectedSystem: string | null,
  pan: { x: number; y: number },
  zoom: number,
) {
  const CX = W / 2 + pan.x;
  const CY = H / 2 + pan.y;
  const baseScale = getScale(state, W, H);
  const scale = baseScale * zoom;

  // Background
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = "#060a14";
  ctx.fillRect(0, 0, W, H);

  // Subtle star field background
  drawStarField(ctx, W, H, pan, zoom);

  // Grid rings
  for (let r = 1; r <= 6; r++) {
    ctx.beginPath();
    ctx.arc(CX, CY, r * scale, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(30, 60, 100, 0.12)";
    ctx.lineWidth = 0.5;
    ctx.stroke();
  }

  // Faction territory regions (soft colored zones behind systems)
  drawFactionTerritories(ctx, state, CX, CY, scale);

  // Trade routes
  drawTradeRoutes(ctx, state, CX, CY, scale);

  // Systems
  for (const sys of state.systems) {
    const sx = CX + sys.position.x * scale;
    const sy = CY - sys.position.y * scale;
    const isCurrent = sys.id === state.player.location;
    const isSelected = sys.id === selectedSystem;
    const starColor = SPECTRAL_COLORS[sys.spectralClass] ?? "#ffffff";
    const factionColor = FACTION_COLORS[sys.faction ?? ""] ?? "#666666";

    // Faction presence halo
    const presences = state.factionPresence.filter(p => p.systemId === sys.id && p.level !== "none");
    if (presences.length > 0) {
      const strongest = presences[0];
      const fc = FACTION_COLORS[strongest.factionId] ?? "#666";
      const haloRadius = strongest.level === "heavy" ? 28 : strongest.level === "moderate" ? 20 : 14;
      const grad = ctx.createRadialGradient(sx, sy, 0, sx, sy, haloRadius * zoom);
      grad.addColorStop(0, fc + "18");
      grad.addColorStop(1, "transparent");
      ctx.beginPath();
      ctx.arc(sx, sy, haloRadius * zoom, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();
    }

    // Selection ring
    if (isSelected) {
      ctx.beginPath();
      ctx.arc(sx, sy, 14 * zoom, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(74, 142, 255, 0.6)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 3]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Current location glow
    if (isCurrent) {
      const grad = ctx.createRadialGradient(sx, sy, 0, sx, sy, 22 * zoom);
      grad.addColorStop(0, "rgba(45, 212, 191, 0.25)");
      grad.addColorStop(1, "transparent");
      ctx.beginPath();
      ctx.arc(sx, sy, 22 * zoom, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();
    }

    // Star dot
    const radius = isCurrent ? 6 : isSelected ? 5 : 4;
    ctx.beginPath();
    ctx.arc(sx, sy, radius * zoom, 0, Math.PI * 2);
    ctx.fillStyle = starColor;
    ctx.fill();

    // Outer ring for faction
    ctx.beginPath();
    ctx.arc(sx, sy, (radius + 2) * zoom, 0, Math.PI * 2);
    ctx.strokeStyle = factionColor + "66";
    ctx.lineWidth = 1;
    ctx.stroke();

    // Recent event marker
    const hasRecentEvent = state.recentEvents.some(e =>
      e.systemId === sys.id && e.visibility === "public"
    );
    if (hasRecentEvent) {
      ctx.beginPath();
      ctx.arc(sx + (radius + 4) * zoom, sy - (radius + 4) * zoom, 2.5 * zoom, 0, Math.PI * 2);
      ctx.fillStyle = "#f59e0b";
      ctx.fill();
    }

    // Label
    const labelAlpha = isCurrent ? 1 : isSelected ? 0.95 : 0.6;
    ctx.fillStyle = `rgba(200, 210, 230, ${labelAlpha})`;
    ctx.font = isCurrent
      ? `bold ${Math.max(9, 10 * zoom)}px 'Courier New', monospace`
      : `${Math.max(8, 9 * zoom)}px 'Courier New', monospace`;
    ctx.fillText(sys.name.toUpperCase(), sx + (radius + 6) * zoom, sy + 3);

    // "YOU" marker
    if (isCurrent) {
      ctx.fillStyle = "rgba(45, 212, 191, 0.8)";
      ctx.font = `bold ${Math.max(7, 7 * zoom)}px 'Courier New', monospace`;
      ctx.fillText("YOU ARE HERE", sx + (radius + 6) * zoom, sy + 3 + 11 * zoom);
    }

    // Region label (only when zoomed in enough)
    if (zoom > 0.8 && !isCurrent) {
      ctx.fillStyle = "rgba(120, 140, 180, 0.35)";
      ctx.font = `${Math.max(7, 7 * zoom)}px 'Courier New', monospace`;
      ctx.fillText(sys.region, sx + (radius + 6) * zoom, sy + 3 + 10 * zoom);
    }
  }

  // Title
  ctx.fillStyle = "rgba(80, 130, 200, 0.6)";
  ctx.font = "bold 11px 'Courier New', monospace";
  ctx.fillText("GAMMA-7 SECTOR — GALACTIC COMMAND", 12, 18);

  // Scale indicator
  const scaleBarLy = 2;
  const scalePx = scaleBarLy * scale;
  ctx.beginPath();
  ctx.moveTo(W - 20 - scalePx, H - 20);
  ctx.lineTo(W - 20, H - 20);
  ctx.strokeStyle = "rgba(80, 130, 200, 0.4)";
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = "rgba(80, 130, 200, 0.4)";
  ctx.font = "7px 'Courier New', monospace";
  ctx.fillText(`${scaleBarLy} ly`, W - 20 - scalePx, H - 24);
}

// Deterministic star field
function drawStarField(
  ctx: CanvasRenderingContext2D,
  W: number, H: number,
  pan: { x: number; y: number },
  _zoom: number,
) {
  const seed = 42;
  let rng = seed;
  const next = () => {
    rng = (rng * 16807 + 0) % 2147483647;
    return rng / 2147483647;
  };

  ctx.fillStyle = "rgba(140, 160, 200, 0.15)";
  for (let i = 0; i < 200; i++) {
    const bx = next() * W * 2 - W * 0.5;
    const by = next() * H * 2 - H * 0.5;
    const parallax = 0.1 + next() * 0.2;
    const sx = bx + pan.x * parallax;
    const sy = by + pan.y * parallax;

    if (sx < 0 || sx > W || sy < 0 || sy > H) continue;
    const size = 0.5 + next() * 1;
    ctx.fillRect(sx, sy, size, size);
  }
}

function drawFactionTerritories(
  ctx: CanvasRenderingContext2D,
  state: GalaxyState,
  CX: number, CY: number,
  scale: number,
) {
  // Group systems by controlling faction
  const factionSystems: Record<string, { x: number; y: number }[]> = {};
  for (const sys of state.systems) {
    const faction = sys.faction;
    if (!faction || faction === "Unaffiliated") continue;
    if (!factionSystems[faction]) factionSystems[faction] = [];
    factionSystems[faction].push({
      x: CX + sys.position.x * scale,
      y: CY - sys.position.y * scale,
    });
  }

  for (const [faction, positions] of Object.entries(factionSystems)) {
    const color = FACTION_COLORS[faction] ?? "#666";
    for (const pos of positions) {
      const grad = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, 60);
      grad.addColorStop(0, color + "0a");
      grad.addColorStop(1, "transparent");
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, 60, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();
    }
  }
}

function drawTradeRoutes(
  ctx: CanvasRenderingContext2D,
  state: GalaxyState,
  CX: number, CY: number,
  scale: number,
) {
  const systemMap = new Map(state.systems.map(s => [s.id, s]));

  for (const route of state.tradeRoutes) {
    const a = systemMap.get(route.systemA);
    const b = systemMap.get(route.systemB);
    if (!a || !b) continue;

    const ax = CX + a.position.x * scale;
    const ay = CY - a.position.y * scale;
    const bx = CX + b.position.x * scale;
    const by = CY - b.position.y * scale;

    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(bx, by);

    if (route.status === "active") {
      ctx.strokeStyle = "rgba(80, 160, 120, 0.15)";
      ctx.lineWidth = 1;
      ctx.setLineDash([]);
    } else if (route.status === "embargoed") {
      ctx.strokeStyle = "rgba(239, 68, 68, 0.2)";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
    } else {
      ctx.strokeStyle = "rgba(251, 191, 36, 0.15)";
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 6]);
    }

    ctx.stroke();
    ctx.setLineDash([]);
  }
}
